import { isUiStatusField } from './fieldTypeAdapter';
import { normalizeFieldOptions } from './fieldOptionsUtils';
import { isStaticBuilderField, isStatusField, isSubStatusField } from './formStudioFormUtils';
import { subStatusFieldHasConfiguredOptions } from './statusOptionSubstatusUtils';
import { OPTION_FIELD_TYPES } from '../constants/formStudioFieldTypes';

export function validateSubStatusFieldOptions(field, fields = []) {
  const parent = fields.find(
    (candidate) =>
      isStatusField(candidate)
      && String(candidate.key_name ?? '').trim() === String(field?.status_parent_key ?? '').trim()
  );
  const parentOptions = normalizeFieldOptions(parent?.key_options);

  if (!subStatusFieldHasConfiguredOptions(field, parentOptions)) {
    return 'Configure sub-status options for at least one status value';
  }

  return '';
}

function validateTextareaLimits(field) {
  const errors = {};
  const min = parseInt(field.minLength, 10);
  const max = parseInt(field.maxLength, 10);

  if (Number.isNaN(min) || min < 0) {
    errors.minLength = 'Min Characters must be a valid non-negative number';
  }
  if (Number.isNaN(max) || max <= 0) {
    errors.maxLength = 'Max Characters must be a valid positive number';
  }
  if (!Number.isNaN(min) && !Number.isNaN(max) && max <= min) {
    errors.maxLength = 'Max Characters must be greater than Min Characters';
  }
  return errors;
}

function validateStatusRules(field, fields, hasFormId = false) {
  const errors = {};

  if (isStatusField(field) && isSubStatusField(field)) {
    errors.statusOption = 'A field cannot be both Status and Sub Status';
    return errors;
  }

  const anySubStatus = fields.some(isSubStatusField);
  const anyStatus = fields.some(isStatusField);

  if (anySubStatus && !anyStatus) {
    errors.statusOption = 'Please create at least one Status field first';
  }

  if (isSubStatusField(field)) {
    if (!String(field.status_parent_key ?? '').trim()) {
      errors.status_parent_key = 'Parent Status Field is required';
    } else {
      const parentIsValid = fields.some(
        (candidate) =>
          isStatusField(candidate)
          && String(candidate.key_name ?? '').trim() === String(field.status_parent_key ?? '').trim()
      );
      if (!parentIsValid) {
        errors.status_parent_key = 'Parent Status is invalid';
      }
    }
  }

  if (isStatusField(field) && hasFormId) {
    if (normalizeFieldOptions(field.key_options).length < 1) {
      errors.key_options = 'Add at least one status option';
    }
  }

  if (isSubStatusField(field) && hasFormId) {
    const subStatusOptionsError = validateSubStatusFieldOptions(field, fields);
    if (subStatusOptionsError) {
      errors.key_options = subStatusOptionsError;
    }
  }

  return errors;
}

function validateDropdownStatusOption(field) {
  const errors = {};
  if (
    OPTION_FIELD_TYPES.includes(field?.key_type)
    && !isStatusField(field)
    && !isSubStatusField(field)
    && String(field?.statusOption ?? '').trim()
  ) {
    errors.statusOption = 'Dropdown fields cannot use status roles';
  }
  if (isUiStatusField(field) && field?.statusOption !== 'is_status') {
    errors.statusOption = 'Status fields must use the Status type';
  }
  return errors;
}

export function validateFormStudioForm(form, { hasFormId = false } = {}) {
  const formErrors = {};
  const fieldErrors = {};
  const errorSummary = [];

  if (!String(form?.leads_form_name ?? '').trim()) {
    formErrors.leads_form_name = 'Form Name is required';
    errorSummary.push('Form Name is required');
  }

  const fields = Array.isArray(form?.leads_form_items) ? form.leads_form_items : [];
  const keyNames = new Set();
  const aliasNames = new Set();

  fields.forEach((field, index) => {
    if (isStaticBuilderField(field)) return;

    const errors = {};

    if (!String(field?.key_name ?? '').trim()) {
      errors.key_name = 'Field Name is required';
      errorSummary.push(`Field #${index + 1}: Field Name is missing`);
    }
    if (!String(field?.alias_name ?? '').trim()) {
      errors.alias_name = 'Alias Name is required';
      errorSummary.push(`Field #${index + 1}: Alias Name is missing`);
    }
    if (!String(field?.key_type ?? '').trim()) {
      errors.key_type = 'Field Type is required';
      errorSummary.push(`Field #${index + 1}: Field Type is missing`);
    }

    const keyName = String(field?.key_name ?? '').trim();
    if (keyName) {
      if (keyNames.has(keyName)) {
        errors.key_name = 'Field Name must be unique';
        errorSummary.push(`Field #${index + 1}: Field Name is duplicated`);
      } else {
        keyNames.add(keyName);
      }
    }

    const aliasName = String(field?.alias_name ?? '').trim();
    if (aliasName) {
      if (aliasNames.has(aliasName)) {
        errors.alias_name = 'Alias Name must be unique';
        errorSummary.push(`Field #${index + 1}: Alias Name is duplicated`);
      } else {
        aliasNames.add(aliasName);
      }
    }

    if (field?.key_type === 'TEXTAREA') {
      Object.assign(errors, validateTextareaLimits(field));
      if (errors.minLength) errorSummary.push(`Field #${index + 1}: Invalid Min Characters`);
      if (errors.maxLength) errorSummary.push(`Field #${index + 1}: Invalid Max Characters`);
    }

    if (field?.is_hot_lead && !String(field?.hot_lead_criteria ?? '').trim()) {
      errors.hot_lead_criteria = 'Hot Record Criteria is required';
      errorSummary.push(`Field #${index + 1}: Hot Record Criteria is required`);
    }

    if (field?.isGroup) {
      const groupTitle = String(field?.group_title ?? '').trim();
      if (!groupTitle && !field?.groupId) {
        errors.group_title = 'Group title or selection is required';
        errorSummary.push(`Field #${index + 1}: Group title or selection is missing`);
      }
    }

    Object.assign(errors, validateStatusRules(field, fields, hasFormId));
    Object.assign(errors, validateDropdownStatusOption(field));

    if (Object.keys(errors).length > 0) {
      fieldErrors[index] = errors;
      Object.values(errors).forEach((message) => {
        errorSummary.push(`Field #${index + 1}: ${message}`);
      });
    }
  });

  const hasErrors = Object.keys(formErrors).length > 0 || Object.keys(fieldErrors).length > 0;

  return {
    isValid: !hasErrors,
    formErrors,
    fieldErrors,
    errorSummary: errorSummary.slice(0, 8),
  };
}
